import { useNavigate } from 'react-router-dom';
import { X, Clock, AlertTriangle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore, BannerVariant } from '@/stores/notificationStore';
import { DEV_MODE } from '@/lib/devMode';

interface TrialBannerProps {
  className?: string;
}

/**
 * TrialBanner - Shows a persistent banner for users approaching trial expiration.
 *
 * Display rules:
 * - Shows when user is in trial (is_trial === true)
 * - Shows when days remaining <= 8
 * - Dismissible with 24-hour snooze
 * - Not shown for admins or subscribed users
 *
 * Variants:
 * - info (blue): 6-8 days remaining
 * - warning (amber): 3-5 days remaining
 * - urgent (red): 1-2 days remaining
 */
export default function TrialBanner({ className }: TrialBannerProps) {
  const { user } = useAuthStore();
  const { dismissBanner, isBannerDismissed } = useNotificationStore();
  const navigate = useNavigate();

  // Skip in dev mode
  if (DEV_MODE) {
    return null;
  }

  // No user or not in trial
  if (!user || !user.is_trial) {
    return null;
  }

  // Admins bypass
  if (user.is_admin) {
    return null;
  }

  // Already subscribed
  if (user.trial_ended) {
    return null; // TrialExpiredModal handles this
  }

  const daysRemaining = user.trial_days_remaining;

  // Only show banner when 8 or fewer days remaining
  if (daysRemaining > 8) {
    return null;
  }

  // Generate banner ID based on days range for snooze logic
  const getBannerId = (): string => {
    if (daysRemaining <= 2) return 'trial-urgent';
    if (daysRemaining <= 5) return 'trial-warning';
    return 'trial-info';
  };

  const bannerId = getBannerId();

  // Check if dismissed within last 24 hours
  if (isBannerDismissed(bannerId)) {
    return null;
  }

  // Determine variant based on days remaining
  const getVariant = (): BannerVariant => {
    if (daysRemaining <= 2) return 'urgent';
    if (daysRemaining <= 5) return 'warning';
    return 'info';
  };

  const variant = getVariant();

  // Get message based on variant
  const getMessage = (): string => {
    if (daysRemaining === 0) {
      return 'Your trial expires today!';
    }
    if (daysRemaining === 1) {
      return 'Your trial expires tomorrow!';
    }
    return `Your trial ends in ${daysRemaining} days`;
  };

  // Styling based on variant
  const variantStyles: Record<BannerVariant, {
    bg: string;
    border: string;
    text: string;
    icon: string;
    button: string;
  }> = {
    info: {
      bg: 'bg-paper dark:bg-blue-950/30',
      border: 'border-hairline dark:border-blue-800',
      text: 'text-ink dark:text-blue-200',
      icon: 'text-steel-dark dark:text-blue-400',
      button: 'bg-ink hover:bg-ink-raised',
    },
    warning: {
      bg: 'bg-white dark:bg-amber-950/30',
      border: 'border-ink dark:border-amber-800',
      text: 'text-ink dark:text-amber-200',
      icon: 'text-ink dark:text-amber-400',
      button: 'bg-ink hover:bg-ink-raised',
    },
    urgent: {
      bg: 'bg-error-bg dark:bg-red-950/30',
      border: 'border-error-border dark:border-red-800',
      text: 'text-error-text dark:text-red-200',
      icon: 'text-error-text dark:text-red-400',
      button: 'bg-error-text hover:opacity-90',
    },
  };

  const styles = variantStyles[variant];

  const Icon = variant === 'urgent' ? AlertCircle : variant === 'warning' ? AlertTriangle : Clock;

  const handleDismiss = () => {
    dismissBanner(bannerId);
  };

  const handleViewPlans = () => {
    navigate('/settings/subscription');
  };

  return (
    <div
      className={`
        ${styles.bg} ${styles.border} ${styles.text}
        border-b px-4 py-3
        ${className || ''}
      `}
      role="alert"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Icon className={`h-5 w-5 flex-shrink-0 ${styles.icon}`} />
          <p className="text-sm font-medium truncate">
            {getMessage()}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={handleViewPlans}
            className={`
              ${styles.button}
              text-white text-sm font-medium
              px-4 py-1.5 rounded-md
              transition-colors
            `}
          >
            View Plans
          </button>

          <button
            onClick={handleDismiss}
            className={`
              p-1 rounded-md
              hover:bg-black/10 dark:hover:bg-white/10
              transition-colors
              ${styles.text}
            `}
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
